"use client"
import { ChevronRight } from "lucide-react"
import Image from "next/image"
import { useTranslations } from "next-intl"

const FeatureCard = ({
  image,
  title,
  description,
  imageScale = "120%",
  accent = "#8B5CF6",
  accentPos = "bottom center",
  topImage,
  bgImage,
}: {
  image: string
  title: string
  description: string
  imageScale?: string
  accent?: string
  accentPos?: string
  topImage?: string
  bgImage?: string
}) => {
  const solidColor = "#13101f"

  return (
    <div className="relative flex flex-col w-full">
      {topImage && (
        <Image
          src={topImage}
          alt=""
          width={150}
          height={150}
          className="opacity-80 absolute right-4 pointer-events-none z-0 w-auto"
          style={{ width: "auto", height: 150, objectFit: "contain", bottom: "100%", marginBottom: "-20px" }}
        />
      )}
      <div className={`h-full relative z-10 bg-[#8a5cf600] ${bgImage ? "" : "rounded-2xl border border-white/5"}`}>
        <div
          className={`${bgImage ? "" : "rounded-2xl"} overflow-hidden flex flex-col h-full w-full`}
          style={bgImage
            ? { backgroundImage: `url('${bgImage}')`, backgroundSize: "100% 100%", backgroundPosition: "center", backgroundRepeat: "no-repeat" }
            : { background: `linear-gradient(to bottom, ${solidColor} 40%, ${solidColor} 40%)` }
          }
        >
          <div className={`relative h-48 w-full overflow-hidden ${bgImage ? "" : "rounded-t-2xl"}`}>
            <div
              className="absolute inset-0"
              style={{
                backgroundImage: `url('${image}')`,
                backgroundSize: imageScale,
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
              }}
            />
            {!bgImage && <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.35)" }} />}
            <div
              className="absolute inset-0"
              style={{ background: `linear-gradient(to bottom, transparent 30%, ${bgImage ? "transparent" : "#13101f"} 100%)` }}
            />
          </div>
          <div className="px-5 pb-6 -mt-2 flex flex-col gap-2 relative">
            <h3 className="text-white font-semibold text-lg font-poppins relative">{title}</h3>
            <p className="text-gray-400 text-sm font-poppins leading-relaxed relative">{description}</p>
          </div>
        </div>
      </div>
    </div>
  )
}

const Features = () => {
  const t = useTranslations('Features')
  
  return (
    <section className="relative z-10 w-full overflow-hidden">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.06) 1px, transparent 0)",
          backgroundSize: "24px 24px",
          maskImage:
            "radial-gradient(ellipse 70% 60% at 50% 45%, #000 20%, transparent 85%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 70% 60% at 50% 45%, #000 20%, transparent 85%)",
        }}
      />



      <div
        className="pointer-events-none absolute bottom-0 left-1/2 -translate-x-1/2 h-[300px] w-[800px] rounded-full opacity-15 blur-3xl"
        style={{ backgroundImage: "radial-gradient(at 50% 100%, #8630c0, transparent)" }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-8 pb-24 relative">
        <div className="mb-10 text-left">
          <h2 className="text-3xl md:text-4xl font-bold font-akira text-white mb-3">{t('title')}</h2>
          <p className="text-gray-400 font-poppins text-base max-w-xl">{t('subtitle')}</p>
        </div>
        <div className="relative">


          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <FeatureCard
              image="https://cdn.dribbble.com/userupload/3372301/file/original-9b20392c81feec7bbedd9e5b63841f45.png?resize=3024x&vertical=center"
              title={t('f1_title')}
              description={t('f1_desc')}
              imageScale="200%"
              accent="#7C3AED"
              accentPos="bottom left"
            />
            <FeatureCard
              image="https://cdn.aurex.lk/Features/game.webp"
              title={t('f2_title')}
              description={t('f2_desc')}
              imageScale="100%"
              accent="#A855F7"
              accentPos="bottom center"
            />
            <FeatureCard
              image="https://cdn.aurex.lk/Features/Location.webp"
              title={t('f3_title')}
              description={t('f3_desc')}
              imageScale="90%"
              accent="#6D28D9"
              accentPos="bottom right"
              topImage="https://cdn.aurex.lk/Features/end-spike.webp"
              bgImage="https://cdn.aurex.lk/fsb.webp"
            />

            <div className="col-span-1 sm:col-span-2">
              <FeatureCard
                image="https://cdn.aurex.lk/Features/supports.webp"
                title={t('f4_title')}
                description={t('f4_desc')}
                imageScale="85%"
                accent="#9b30c0"
                accentPos="bottom center"
              />

            </div>
            <FeatureCard
              image="https://cdn.aurex.lk/Features/ddos.webp"
              title={t('f5_title')}
              description={t('f5_desc')}
              imageScale="110%"
              accent="#6D28D9"
              accentPos="bottom center"
            />
          </div>
        </div>
      </div>
    </section>
  )
}

export default Features
